/**
 * The Vera "V" mark, as an icon.
 *
 * Ask Vera is the product's own feature, so it is marked with the product's own logo rather
 * than a borrowed AI cliché (it was lucide's `SparklesIcon`, hand-imported at three call
 * sites). Extracted on the third hand-roll — working-conventions §36.
 *
 * The path is INLINED rather than imported from `packages/ui/assets/vlogo.svg`, because the
 * point of this component is that it inherits colour: an `<img src=…>` renders the file's own
 * `fill` and cannot be tinted by CSS. `fill="currentColor"` (the asset's `#272727` is
 * deliberately dropped) makes it behave like a lucide glyph — callers set `color` on a wrapper
 * or a parent button and the mark follows.
 *
 * `size` is the WIDTH, matching how lucide's `size` is read at the call sites this replaces.
 * The mark is wider than it is tall (172 × 148), so height is derived from the aspect ratio
 * instead of being forced square, which would squash it.
 */
import React from 'react'

/** The asset's intrinsic dimensions — the viewBox this path was drawn in. */
const VIEWBOX_WIDTH = 172
const VIEWBOX_HEIGHT = 148

const V_PATH =
  'M84.0345 79.33L39.6849 1.77528C39.3364 1.17763 38.6829 0.802368 37.9568 0.802368H2.39299C0.897245 0.802368 -0.0466709 2.34512 0.679419 3.59601L84.0345 147.031C84.7896 148.323 86.721 148.323 87.4616 147.031L170.817 3.59601C171.543 2.34512 170.599 0.802368 169.103 0.802368H133.539C132.828 0.802368 132.16 1.17763 131.811 1.77528L87.4616 79.33C86.721 80.6365 84.7606 80.6365 84.02 79.33H84.0345Z'

export type AskVeraIconProps = {
  /** Rendered width in px; height follows the mark's aspect ratio. Lucide's default. */
  size?: number
  /**
   * An accessible name. Omit it (the default) for a decorative mark sitting next to its own
   * label — every current call site does, so the icon stays out of the accessibility tree
   * rather than reading the label twice.
   */
  title?: string
  className?: string
}

export const AskVeraIcon = ({ size = 24, title, className }: AskVeraIconProps) => (
  <svg
    className={className}
    width={size}
    height={(size * VIEWBOX_HEIGHT) / VIEWBOX_WIDTH}
    viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role={title ? 'img' : undefined}
    aria-hidden={title ? undefined : true}
    focusable="false"
  >
    {title && <title>{title}</title>}
    <path d={V_PATH} fill="currentColor" />
  </svg>
)
